import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, Calendar, Leaf, Fish, Egg, Wheat, Camera, Upload, ChevronRight, Activity, TrendingUp, Clock, Droplets } from 'lucide-react';

const PACKAGE_GUIDES: Record<string, { title: string; description: string; icon: React.ElementType; tips: string[] }> = {
  fish: {
    title: 'Fish Farming Guide',
    description: 'Complete guide for catfish and tilapia cultivation',
    icon: Fish,
    tips: [
      'Maintain water pH between 6.5-8.0',
      'Feed 3% of body weight daily, split into 2-3 meals',
      'Check dissolved oxygen levels morning and evening',
      'Perform 20% water change weekly',
      'Monitor for signs of disease daily'
    ]
  },
  poultry: {
    title: 'Poultry Farming Guide',
    description: 'Broiler and layer management best practices',
    icon: Egg,
    tips: [
      'Maintain brooding temperature at 32-35°C for first week',
      'Provide 23 hours of light for first 3 days',
      'Ensure 1.5cm of feeder space per chick',
      'Vaccinate on days 7, 14, and 21',
      'Keep litter dry and clean'
    ]
  },
  rabbits: {
    title: 'Rabbit Farming Guide',
    description: 'Breeding and grow-out management',
    icon: Leaf,
    tips: [
      'Provide 15% protein pellets plus unlimited hay',
      'Keep breeding stock at 18-24°C',
      'Doe kindles after 30-32 days gestation',
      'Wean kits at 4-5 weeks',
      'Check for signs of snuffles weekly'
    ]
  },
  default: {
    title: 'General Farming Guide',
    description: 'Getting started with your farm',
    icon: Wheat,
    tips: [
      'Start with a small batch to learn',
      'Keep detailed records of all activities',
      'Monitor weather conditions daily',
      'Build relationship with local vet',
      'Plan your market before harvesting'
    ]
  }
};

const WEEKLY_CHECKLIST: Record<string, string[]> = {
  week1: [
    'Set up housing/tank/pond properly',
    'Acclimate fingerlings/chicks to new environment',
    'Install water/feed systems',
    'Set up aeration equipment',
    'Complete first feeding schedule',
    'Record baseline stock count and weights'
  ],
  week2: [
    'Monitor feeding response and adjust quantities',
    'Check water quality parameters',
    'Observe behavior for signs of stress',
    'Clean feeding equipment',
    'Record daily mortality if any',
    'Contact support with any concerns'
  ],
  week3: [
    'Gradually increase feed quantity',
    'Check growth rates with sample weighing',
    'Clean and disinfect housing',
    'Top up water/nutrient levels',
    'Review and adjust lighting/temperature',
    'Plan for week 4 health check'
  ],
  week4: [
    'Complete first month health assessment',
    'Calculate feed conversion ratio',
    'Plan next month feed requirements',
    'Schedule vet check if needed',
    'Review and log all observations',
    'Set goals for month 2'
  ]
};

const RED_FLAG_SYMPTOMS = [
  { id: 'lethargy', label: 'Fish/Birds are unusually lethargic or inactive', severity: 'high' },
  { id: 'no-feed', label: 'Refusing to eat for more than 24 hours', severity: 'high' },
  { id: 'abnormal-swim', label: 'Abnormal swimming/balance issues', severity: 'high' },
  { id: 'skin-lesions', label: 'Visible spots, lesions, or ulcers on skin', severity: 'medium' },
  { id: 'rapid-gill', label: 'Rapid gill/fast breathing at rest', severity: 'high' },
  { id: 'discolored', label: 'Discolored body or fins', severity: 'medium' },
  { id: 'bloating', label: 'Abdominal swelling or bloating', severity: 'medium' },
  { id: 'feather-loss', label: 'Excessive feather/scale loss', severity: 'low' },
  { id: 'coughing', label: 'Coughing, sneezing, or nasal discharge', severity: 'medium' },
  { id: 'dropping', label: 'Unusual droppings (bloody/green)', severity: 'medium' }
];

const GROWTH_STAGES = {
  fish: ['Fingerling', 'Juvenile', 'Grow-out', 'Pre-harvest', 'Market Size'],
  poultry: ['Day-old', 'Brooder', 'Grower', 'Finisher', 'Market Ready'],
  rabbits: ['Weaner', 'Grower', 'Breeder', 'Market Weight'],
  default: ['Day 1', 'Week 1', 'Week 2', 'Month 1', 'Market Ready']
};

interface FarmData {
  packageType: string;
  stockCount: number;
  daysSinceStocking: number;
  feedRemaining: number;
  feedUnit: string;
}

export function PostPurchasePage() {
  const [activeTab, setActiveTab] = useState('guide');
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [checkedSymptoms, setCheckedSymptoms] = useState<string[]>([]);
  const [farmData, setFarmData] = useState<FarmData>({
    packageType: 'fish',
    stockCount: 500,
    daysSinceStocking: 1,
    feedRemaining: 2,
    feedUnit: 'bags'
  });
  const [harvestInputs, setHarvestInputs] = useState({ days: 90, feedRemaining: 10 });
  const [harvestResult, setHarvestResult] = useState<{ date: string; stage: string } | null>(null);
  const [successStory, setSuccessStory] = useState({ name: '', story: '', file: null as File | null });
  const [packageType, setPackageType] = useState('fish');

  const guide = PACKAGE_GUIDES[packageType] || PACKAGE_GUIDES.default;
  const growthStages = GROWTH_STAGES[packageType as keyof typeof GROWTH_STAGES] || GROWTH_STAGES.default;

  useEffect(() => {
    const today = new Date();
    const harvestDate = new Date(today.getTime() + harvestInputs.days * 24 * 60 * 60 * 1000);
    const stageIndex = Math.min(Math.floor(harvestInputs.days / 20), growthStages.length - 1);
    setHarvestResult({
      date: harvestDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      stage: growthStages[stageIndex]
    });
  }, [harvestInputs.days, harvestInputs.feedRemaining, growthStages]);

  const toggleChecklist = (item: string) => {
    setChecklist(prev => ({ ...prev, [item]: !prev[item] }));
  };

  const toggleSymptom = (id: string) => {
    setCheckedSymptoms(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSuccessStory(prev => ({ ...prev, file: e.target.files![0] }));
    }
  };

  const handleSubmitStory = () => {
    alert('Story submitted successfully! Thank you for sharing your farming journey.');
    setSuccessStory({ name: '', story: '', file: null });
  };

  const progressPercentage = Object.values(checklist).filter(Boolean).length / 6 * 100;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Home</Link>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">My Farm Guide</span>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Leaf className="h-6 w-6 text-green-600" />
            Post-Purchase Success System
          </h1>
          <p className="text-muted-foreground mt-1">Your personalized farming companion</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="guide" className="text-xs">Guide</TabsTrigger>
            <TabsTrigger value="checklist" className="text-xs">Checklist</TabsTrigger>
            <TabsTrigger value="monitor" className="text-xs">Monitor</TabsTrigger>
            <TabsTrigger value="checker" className="text-xs">Health</TabsTrigger>
            <TabsTrigger value="harvest" className="text-xs">Harvest</TabsTrigger>
          </TabsList>

          <TabsContent value="guide" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <guide.icon className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle>{guide.title}</CardTitle>
                      <CardDescription>{guide.description}</CardDescription>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => {
                    const types = ['fish', 'poultry', 'rabbits'];
                    const next = types[(types.indexOf(packageType) + 1) % types.length];
                    setPackageType(next);
                  }}>
                    Switch Guide
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-3">Quick Start Tips</h3>
                    <div className="grid gap-3">
                      {guide.tips.map((tip, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="checklist" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Week 1-4 Setup Checklist</CardTitle>
                <CardDescription>Track your progress through the first month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium">Overall Progress</span>
                  <span className="text-sm text-muted-foreground">{Math.round(progressPercentage)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full mb-6">
                  <div className="h-2 bg-green-600 rounded-full" style={{ width: `${progressPercentage}%` }} />
                </div>

                {Object.entries(WEEKLY_CHECKLIST).map(([week, items]) => (
                  <div key={week} className="mb-6">
                    <h3 className="font-semibold capitalize flex items-center gap-2 mb-3">
                      <Badge variant="outline">{week.replace('week', 'Week ')}</Badge>
                    </h3>
                    <div className="space-y-2">
                      {items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <Checkbox
                            id={`${week}-${idx}`}
                            checked={checklist[`${week}-${idx}`] || false}
                            onCheckedChange={() => toggleChecklist(`${week}-${idx}`)}
                          />
                          <Label
                            htmlFor={`${week}-${idx}`}
                            className="text-sm cursor-pointer"
                          >
                            {item}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="monitor" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Farm Monitoring Dashboard</CardTitle>
                <CardDescription>Track your farm's daily metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="packageType">Farm Type</Label>
                      <select
                        id="packageType"
                        className="w-full mt-1 p-2 border rounded-md"
                        value={packageType}
                        onChange={(e) => setPackageType(e.target.value)}
                      >
                        <option value="fish">Fish Farming</option>
                        <option value="poultry">Poultry</option>
                        <option value="rabbits">Rabbit Farming</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="stockCount">Stock Count</Label>
                      <Input
                        id="stockCount"
                        type="number"
                        value={farmData.stockCount}
                        onChange={(e) => setFarmData(prev => ({ ...prev, stockCount: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="daysSinceStocking">Days Since Stocking</Label>
                      <Input
                        id="daysSinceStocking"
                        type="number"
                        value={farmData.daysSinceStocking}
                        onChange={(e) => setFarmData(prev => ({ ...prev, daysSinceStocking: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="feedRemaining">Feed Remaining</Label>
                      <div className="flex gap-2">
                        <Input
                          id="feedRemaining"
                          type="number"
                          value={farmData.feedRemaining}
                          onChange={(e) => setFarmData(prev => ({ ...prev, feedRemaining: parseFloat(e.target.value) || 0 }))}
                        />
                        <select
                          className="p-2 border rounded-md"
                          value={farmData.feedUnit}
                          onChange={(e) => setFarmData(prev => ({ ...prev, feedUnit: e.target.value }))}
                        >
                          <option value="bags">bags</option>
                          <option value="kg">kg</option>
                        </select>
                      </div>
                    </div>

                    <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Growth Stage
                      </h4>
                      <div className="flex gap-1 flex-wrap">
                        {growthStages.map((stage, idx) => {
                          const currentStage = Math.min(Math.floor(farmData.daysSinceStocking / 20), growthStages.length - 1);
                          return (
                            <Badge
                              key={stage}
                              variant={idx === currentStage ? 'default' : 'outline'}
                              className="text-xs"
                            >
                              {stage}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <Fish className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                        <div className="text-lg font-bold">{farmData.stockCount}</div>
                        <div className="text-xs text-blue-600">Stock Count</div>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <Clock className="h-5 w-5 mx-auto text-orange-600 mb-1" />
                        <div className="text-lg font-bold">{farmData.daysSinceStocking}</div>
                        <div className="text-xs text-orange-600">Days</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="checker" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Red Flag Symptom Checker</CardTitle>
                <CardDescription>Check for signs that need immediate attention</CardDescription>
              </CardHeader>
              <CardContent>
                {checkedSymptoms.length > 0 && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                      <AlertCircle className="h-5 w-5" />
                      Attention Required
                    </div>
                    <p className="text-sm text-red-700 mb-3">
                      You have {checkedSymptoms.length} symptom(s) checked. Please contact a veterinarian immediately.
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive">Call Emergency Vet</Button>
                      <Button size="sm" variant="outline" asChild>
                        <a href="tel:+233">Call FarmPack Support</a>
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {RED_FLAG_SYMPTOMS.map((symptom) => (
                    <div
                      key={symptom.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        checkedSymptoms.includes(symptom.id)
                          ? 'border-red-300 bg-red-50'
                          : 'border-muted'
                      }`}
                    >
                      <Checkbox
                        id={symptom.id}
                        checked={checkedSymptoms.includes(symptom.id)}
                        onCheckedChange={() => toggleSymptom(symptom.id)}
                      />
                      <Label htmlFor={symptom.id} className="flex-1 cursor-pointer text-sm">
                        {symptom.label}
                      </Label>
                      <Badge
                        variant={symptom.severity === 'high' ? 'destructive' : symptom.severity === 'medium' ? 'outline' : 'secondary'}
                        className="text-xs"
                      >
                        {symptom.severity}
                      </Badge>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-semibold flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4" />
                    Schedule Weekly Check-in
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Get SMS reminders for weekly farm check-ups
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">Monday</Button>
                    <Button size="sm" variant="outline">Wednesday</Button>
                    <Button size="sm" variant="outline">Friday</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="harvest" className="mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Harvest Planning Calculator</CardTitle>
                  <CardDescription>Estimate your projected harvest date</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="harvestDays">Days to Harvest</Label>
                    <Input
                      id="harvestDays"
                      type="number"
                      value={harvestInputs.days}
                      onChange={(e) => setHarvestInputs(prev => ({ ...prev, days: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="feedForCalc">Feed Bags Remaining</Label>
                    <Input
                      id="feedForCalc"
                      type="number"
                      value={harvestInputs.feedRemaining}
                      onChange={(e) => setHarvestInputs(prev => ({ ...prev, feedRemaining: parseInt(e.target.value) || 0 }))}
                    />
                  </div>

                  {harvestResult && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg mt-4">
                      <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
                        <CheckCircle2 className="h-5 w-5" />
                        Projected Results
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><strong>Harvest Date:</strong> {harvestResult.date}</p>
                        <p><strong>Expected Stage:</strong> {harvestResult.stage}</p>
                        {harvestInputs.feedRemaining < 5 && (
                          <p className="text-orange-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Low feed! Consider ordering more
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Share Your Success Story</CardTitle>
                  <CardDescription>Inspire other farmers and earn ₵100 credit</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="storyName">Your Name</Label>
                    <Input
                      id="storyName"
                      placeholder="Enter your name"
                      value={successStory.name}
                      onChange={(e) => setSuccessStory(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="storyText">Your Story</Label>
                    <textarea
                      id="storyText"
                      className="w-full p-3 border rounded-md min-h-[100px]"
                      placeholder="Share your farming journey..."
                      value={successStory.story}
                      onChange={(e) => setSuccessStory(prev => ({ ...prev, story: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Upload Photo (Optional)</Label>
                    <div className="border-2 border-dashed border-muted rounded-lg p-4 text-center mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <Camera className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {successStory.file ? successStory.file.name : 'Click to upload photo'}
                        </p>
                      </label>
                    </div>
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleSubmitStory}
                    disabled={!successStory.name || !successStory.story}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Submit Story
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}